import React, { useState } from "react";
import { motion } from "framer-motion";
import LucideIcon from "utils/LucideIcon";
import { IPrivacyAndTermsPage } from "Interfaces/interface.informationalPages";

const TermsOfUsePage: React.FC<IPrivacyAndTermsPage> = ({
  websiteDomain = "gifthub.360customizer.com",
  companyName = "360Customizer",
  companyAddress = "2nd Floor A wing, Pathak Park, 360Customizer (Namrup Technologies Pvt Ltd), next to Tech Mahindra, behind Anand veg restaurant, Bhalekar Chawl, Erandwane, Pune, Maharashtra 411004",
  officerName = "Vikas Tiwari",
  officerEmail = "vikas.tiwari@360customizer.com",
  companyEmail = "sales@360customizer.com",
  companyContact = "+91-8637758689",
}) => {
  const [activeSection, setActiveSection] = useState<string>("introduction");

  const termsOfUsaSectionData = [
    {
      id: "introduction",
      title: "Introduction",
      content: [
        `This document serves as an electronic record under the Information Technology Act, 2000, and the applicable rules under this Act, including any amendments made to electronic records under various statutes by the Information Technology Act, 2000. It is generated through a computer system and does not require any physical or digital signatures.`,
        `The domain name ${websiteDomain} (hereinafter referred to as the "Website") is owned by ${companyName}, a company with its registered office located at ${companyAddress}.`,
        `${companyName} operates Gifthub, a comprehensive corporate gifting platform that enables businesses to discover, customize, and inquire about personalized products. Our platform includes AI-powered recommendations, real-time mockup generation, and professional catalog creation services.`,
        `These Terms of Use govern your access to and use of our platform, including all features, services, and content available through the Website.`,
      ],
    },
    {
      id: "service-description",
      title: "Service Description",
      content: [
        `Corporate Gifting Platform: We provide a comprehensive platform for businesses to discover, customize, and inquire about personalized corporate gifts from our extensive product catalog.`,
        `AI-Powered Features: Our platform includes an AI assistant that provides intelligent product recommendations, automated catalog generation, and personalized suggestions based on your requirements.`,
        `Customization Services: We offer real-time mockup generation, logo placement tools, color customization, and bulk customization capabilities for multiple products.`,
        `Inquiry Management: Our platform supports inquiry submission functionality, product customization, and catalog generation for client presentations.`,
        `Catalog Generation: We provide professional PDF catalog generation services with company branding and customizable templates for client presentations.`,
        `User Management: We offer multi-role user systems with company profile management and user verification.`,
      ],
    },
    {
      id: "eligibility",
      title: "Eligibility and Account Requirements",
      content: [
        `Age Requirement: You must be at least 18 years old to use our platform. If you are under 18, you may not register for an account or use our services.`,
        `Business Authorization: Corporate users must have proper authorization from their organization to make purchases and use corporate features.`,
        `Accurate Information: You must provide accurate, current, and complete information during registration and keep your account information updated.`,
        `Single Account: Each individual or business entity may maintain only one account. Multiple accounts for the same entity are prohibited.`,
        `Email Verification: All accounts require email verification for activation. You must have access to the email address provided during registration.`,
        `Prohibited Users: Individuals who are "incompetent to contract" under the Indian Contract Act, 1872, including minors, undischarged insolvents, and others, are not eligible to use the platform.`,
      ],
    },
    {
      id: "account-responsibilities",
      title: "Account Responsibilities",
      content: [
        `Password Security: You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.`,
        `Account Monitoring: You must monitor your account for any unauthorized use and notify us immediately of any security breaches or suspicious activity.`,
        `Information Updates: You must promptly update your account information to ensure it remains accurate, current, and complete.`,
        `Account Suspension: We reserve the right to suspend, terminate, or block access to your account if we have reasonable grounds to believe your information is incorrect or if you violate these terms.`,
        `Notification Requirements: You must notify us immediately of any changes to your contact information, business status, or authorization to use corporate features.`,
      ],
    },
    {
      id: "acceptable-use",
      title: "Acceptable Use Policy",
      content: [
        `Permitted Uses: You may use our platform to browse products, create customizations, submit inquiries, generate catalogs, and interact with our AI assistant for legitimate business purposes.`,
        `File Uploads: You may upload company logos and custom designs in supported formats (PNG, JPG, SVG). You must own or have proper rights to all uploaded content.`,
        `Prohibited Activities: You may not use our platform for any illegal activities, to violate intellectual property rights, or to interfere with the platform's operation.`,
        `Content Restrictions: You may not upload content that is illegal, harmful, threatening, abusive, defamatory, or violates any third-party rights.`,
        `System Integrity: You may not attempt to reverse engineer, hack, or compromise our platform's security or functionality.`,
        `Automated Access: You may not use automated tools, bots, or scripts to access our platform without our express written permission.`,
        `Spam and Abuse: You may not use our platform to send spam, unsolicited communications, or engage in any form of harassment or abuse.`,
      ],
    },
    {
      id: "intellectual-property",
      title: "Intellectual Property Rights",
      content: [
        `User Content: You retain ownership of logos, designs, and other content you upload to our platform. By uploading content, you grant us a license to use it for providing our services.`,
        `Platform Content: ${companyName} owns all rights to the platform's code, design, functionality, and proprietary technology. You may not copy, modify, or distribute our platform content.`,
        `Product Images: Product images and descriptions are owned by respective manufacturers and suppliers. We display them under license for your viewing and ordering purposes.`,
        `AI-Generated Content: Recommendations, suggestions, and AI-generated catalogs created by our platform are owned by ${companyName}.`,
        `Trademarks: "360Customizer," "Gifthub," and related trademarks are protected intellectual property of ${companyName}.`,
        `Copyright Compliance: You must respect third-party copyrights and intellectual property rights. We will respond to valid copyright infringement notices.`,
      ],
    },
    {
      id: "inquiry-terms",
      title: "Inquiry Terms and Conditions",
      content: [
        `Inquiry Submission: You may submit inquiries for products through our platform. All inquiries are subject to review and response by our team.`,
        `Pricing Information: Pricing information displayed on our platform is for reference purposes only. Final pricing will be provided during the inquiry response process.`,
        `Inquiry Response: We will respond to your inquiries within 2-3 business days. Response times may vary based on inquiry complexity and volume.`,
        `Customization Requests: Customization requests submitted through inquiries will be evaluated for feasibility and pricing.`,
        `Follow-up Process: After initial inquiry response, further communication and order processing will be handled through direct communication channels.`,
        `Inquiry Validity: Inquiries are valid for 30 days from submission. After this period, you may need to resubmit your inquiry.`,
      ],
    },
    {
      id: "ai-services",
      title: "AI Services and Features",
      content: [
        `AI Assistant: Our AI assistant provides product recommendations and assistance. AI responses are generated based on available data and may not always be accurate or complete.`,
        `Recommendation Accuracy: While we strive for accuracy, AI-generated recommendations are suggestions only and should not be considered as professional advice.`,
        `Data Processing: Your interactions with our AI assistant may be processed to improve our AI algorithms and provide better recommendations.`,
        `AI Limitations: Our AI services are provided "as is" and may have limitations in understanding complex requirements or providing perfect recommendations.`,
        `Human Oversight: Important business decisions should not rely solely on AI recommendations. We recommend human review of all AI-generated content.`,
        `Opt-out Rights: You may opt out of AI-powered features and data processing by contacting our support team.`,
      ],
    },
    {
      id: "service-availability",
      title: "Service Availability and Limitations",
      content: [
        `Uptime Target: We aim for 99.9% platform availability, excluding scheduled maintenance windows and force majeure events.`,
        `Service Credits: Extended downtime beyond our control may result in service credits as outlined in our Service Level Agreement.`,
        `Maintenance: We may suspend services for maintenance, updates, or improvements. We will provide advance notice when possible.`,
        `Feature Changes: We reserve the right to modify, suspend, or discontinue any features or services at our discretion.`,
        `Third-Party Dependencies: Our platform depends on third-party services. Service interruptions from these providers may affect our platform.`,
        `Geographic Restrictions: Some features may not be available in all geographic locations due to legal or technical constraints.`,
      ],
    },
    {
      id: "limitation-liability",
      title: "Limitation of Liability",
      content: [
        `Maximum Liability: Our total liability for any claims arising from your use of our platform is limited to the amount you paid for our services in the 12 months preceding the claim.`,
        `Excluded Damages: We are not liable for indirect, incidental, special, consequential, or punitive damages, including lost profits, data loss, or business interruption.`,
        `Force Majeure: We are not liable for delays or failures caused by circumstances beyond our reasonable control, including natural disasters, government actions, or third-party service failures.`,
        `User Responsibility: You assume responsibility for your use of our platform and any decisions made based on our services or AI recommendations.`,
        `Product Liability: We are not liable for issues arising from third-party products or services. Product warranties are provided by respective manufacturers.`,
        `Indemnification: You agree to indemnify and hold us harmless from any claims arising from your violation of these terms or misuse of our platform.`,
      ],
    },
    {
      id: "dispute-resolution",
      title: "Dispute Resolution",
      content: [
        `Governing Law: These terms are governed by the laws of India, without regard to conflict of law principles.`,
        `Arbitration: Any disputes arising from these terms or your use of our platform shall be resolved through binding arbitration in accordance with the Arbitration & Conciliation Act, 1996.`,
        `Arbitration Location: Arbitration proceedings shall be conducted in Delhi, India, in the English language.`,
        `Arbitrator Selection: The arbitrator shall be an independent and neutral third party selected by mutual agreement or appointed by a competent court.`,
        `Class Action Waiver: You waive any right to participate in class action lawsuits or class-wide arbitration against us.`,
        `Mediation: Before arbitration, parties may attempt to resolve disputes through good faith negotiation and mediation.`,
      ],
    },
    {
      id: "general-terms",
      title: "General Terms",
      content: [
        `Entire Agreement: These terms constitute the entire agreement between you and ${companyName} regarding your use of our platform.`,
        `Severability: If any provision of these terms is found to be unenforceable, the remaining provisions will remain in full force and effect.`,
        `Waiver: Our failure to enforce any provision of these terms does not constitute a waiver of that provision or any other provision.`,
        `Assignment: You may not assign or transfer your rights under these terms without our written consent. We may assign our rights and obligations.`,
        `Survival: Provisions relating to intellectual property, limitation of liability, and dispute resolution shall survive termination of these terms.`,
        `Updates: We may update these terms periodically. Material changes will be communicated through email or platform notifications.`,
      ],
    },
    {
      id: "grievance",
      title: "Grievance Officer",
      content: [
        `Complaint Process: Any complaints regarding violations of these Terms of Use should be addressed to our designated Grievance Officer.`,
        `Response Timeline: We are committed to addressing grievances promptly and will respond to complaints within 30 days of receipt.`,
        `Contact Information: Please refer to the contact details provided below for submitting grievances or concerns.`,
      ],
      includeContact: true,
    },
  ];

  const sectionData = termsOfUsaSectionData.find((s: any) => s.id === activeSection);

  const sections = [
    { id: "introduction", title: "Introduction" },
    { id: "service-description", title: "Service Description" },
    { id: "eligibility", title: "Eligibility and Account Requirements" },
    { id: "account-responsibilities", title: "Account Responsibilities" },
    { id: "acceptable-use", title: "Acceptable Use Policy" },
    { id: "intellectual-property", title: "Intellectual Property Rights" },
    { id: "inquiry-terms", title: "Inquiry Terms and Conditions" },
    { id: "ai-services", title: "AI Services and Features" },
    { id: "service-availability", title: "Service Availability and Limitations" },
    { id: "limitation-liability", title: "Limitation of Liability" },
    { id: "dispute-resolution", title: "Dispute Resolution" },
    { id: "general-terms", title: "General Terms" },
    { id: "grievance", title: "Grievance Officer" },
  ];

  const isActive = (sectionId: string) => activeSection === sectionId;

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  return (
    <motion.div
      className="min-h-screen"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <motion.div className="overflow-hidden md:p-8" variants={itemVariants}>
          {/* Header */}
          <motion.h1
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="flex items-center md:text-left justify-center md:justify-start mb-4">
              <LucideIcon
                name="FileText"
                className="w-10 h-10 text-blue-900 hover:text-blue-900 hidden md:inline-block mr-3"
              />
              <h1 className="text-4xl md:text-5xl font-bold font-inter text-secondary-gradient bg-clip-text text-transparent pb-1">
                Terms of Use
              </h1>
            </div>
          </motion.h1>
          <motion.div
            className="w-24 h-1 bg-secondary-gradient mx-auto md:mx-0 md:ml-14 mb-8 rounded-xl"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ amount: 0.2 }}
          ></motion.div>

          {/* Content */}
          <div>
            <motion.div
              className="prose prose-lg max-w-none"
              variants={itemVariants}
            >
              {/* Introduction Text */}
              <motion.p
                className="text-gray-700 leading-relaxed mb-8 text-base font-inter"
                variants={itemVariants}
              >
                Welcome to{" "}
                <span className="font-semibold text-indigo-400">
                  Gifthub
                </span>
                . These terms and conditions outline the rules and regulations
                for the use of Gifthub's Website, located at{" "}
                <span className="font-semibold text-indigo-400">
                  {websiteDomain}
                </span>
                . By accessing this website, we assume you accept these terms
                and conditions. Do not continue to use {websiteDomain} if you do
                not agree to take all of the terms and conditions stated on this
                page.
              </motion.p>

              <motion.div
                className="bg-gradient-to-r from-blue-100 to-indigo-100 border-l-4 border-red-500 p-6 rounded-r-lg mb-8"
                variants={itemVariants}
              >
                <h4 className="font-bold text-red-500 text-xl font-inter mb-2">
                  IMPORTANT NOTICE
                </h4>
                <p className="text-red-400 font-inter">
                  ACCESSING, BROWSING, OR OTHERWISE USING THE SITE INDICATES
                  YOUR AGREEMENT TO ALL THE TERMS AND CONDITIONS. PLEASE READ
                  THE TERMS AND CONDITIONS CAREFULLY BEFORE PROCEEDING.
                </p>
              </motion.div>

              {/* Navigation */}
              <motion.div
                className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-8"
                variants={itemVariants}
              >
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${isActive(section.id)
                        ? "bg-indigo-600 text-white shadow-lg"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                  >
                    {section.title}
                  </button>
                ))}
              </motion.div>

              {/* Content Sections */}
              {sectionData && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="bg-indigo-50 rounded-xl p-6"
                >
                  <h4 className="text-2xl font-bold font-inter text-secondary-gradient bg-clip-text text-transparent pb-1 mb-4">
                    {sectionData.title}
                  </h4>
                  <div className="space-y-4 text-gray-700 font-inter text-base">
                    {sectionData.content.map((para, index) => (
                      <p key={index}>{para}</p>
                    ))}
                  </div>

                  {sectionData.includeContact && (
                    <div className="bg-white rounded-lg p-4 border border-indigo-200 mt-6">
                      <h5 className="font-bold text-xl text-gray-800 mb-3">
                        Contact Information
                      </h5>
                      <div className="text-gray-700">
                        <p className="font-inter text-base">
                          <strong>Officer Incharge:</strong> {officerName} (
                          {companyName})
                        </p>
                        <p className="font-inter text-base">
                          <strong>Email:</strong> {officerEmail}
                        </p>
                        <p className="font-inter text-base">
                          <strong>Phone:</strong> {companyContact}
                        </p>
                        <p className="font-inter text-base">
                          <strong>Timings:</strong> 10 AM to 6 PM, Monday to
                          Saturday, except public holidays
                        </p>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default TermsOfUsePage;